import React from "react";
import { User } from "../../types";
import { useApp } from "../../context/AppContext";
import { traitLevelColor } from "../../utils/scoring";
import GlassCard from "../../components/ui/GlassCard";
import StatCard from "../../components/ui/StatCard";
import Button from "../../components/ui/Button";
import Badge from "../../components/ui/Badge";
import ProgressBar from "../../components/ui/ProgressBar";
import PageShell from "../../components/layout/PageShell";

interface Props { user: User; onNavigate: (tab: string) => void; }

const CandidateDashboard: React.FC<Props> = ({ user, onNavigate }) => {
  const { results, exams, assignments, traits } = useApp();

  const myAssignments = assignments.filter(a => a.candidateId === user.id);
  const myResults     = results.filter(r => r.candidateId === user.id);
  const pending       = myAssignments.filter(a => a.status === "pending");
  const inProgress    = myAssignments.filter(a => a.status === "in_progress");

  return (
    <PageShell title={`Welcome back, ${user.name.split(" ")[0]}!`} subtitle="Your personalised assessment dashboard">
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Assigned"   value={myAssignments.length}    icon="📋" color="#6366f1" onClick={() => onNavigate("exams")} />
        <StatCard label="Completed"  value={myResults.length}        icon="✅" color="#10b981" onClick={() => onNavigate("results")} />
        <StatCard label="Pending"    value={pending.length}          icon="⏳" color="#f59e0b" onClick={() => onNavigate("exams")} />
      </div>

      {/* Psychometric profile */}
      {myResults[0]?.traitScores && (
        <GlassCard className="p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-extrabold text-gray-800">🧠 My Psychometric Profile</h3>
            <Button size="sm" variant="secondary" onClick={() => onNavigate("results")}>Full Report →</Button>
          </div>
          <div className="space-y-4">
            {myResults[0].traitScores.map(ts => {
              const trait = traits.find(t => t.id === ts.traitId);
              const color = traitLevelColor(ts.level);
              return (
                <div key={ts.traitId} className="flex items-center gap-4">
                  <div className="flex items-center gap-2 w-44 flex-shrink-0">
                    <span className="text-xl">{trait?.name}</span>
                    <span className="text-sm font-bold text-gray-700">{ts.traitName}</span>
                  </div>
                  <div className="flex-1">
                    <ProgressBar value={ts.score} max={ts.maxScore} color={ color} height={10} />
                  </div>
                  <span className="text-sm font-extrabold text-gray-800 w-12 text-right">{ts.percentage}%</span>
                  <Badge text={ts.level} />
                </div>
              );
            })}
          </div>
        </GlassCard>
      )}

      {/* Pending exams */}
      <GlassCard className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-extrabold text-gray-800">Upcoming Exams</h3>
          <Button size="sm" variant="secondary" onClick={() => onNavigate("exams")}>View All →</Button>
        </div>
        {[...pending, ...inProgress].length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <div className="text-4xl mb-2">🎉</div>
            <p className="text-sm">All caught up! No pending exams.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {[...inProgress, ...pending].slice(0, 3).map(a => {
              const exam = exams.find(e => e.id === a.examId);
              if (!exam) return null;
              return (
                <div key={a.id}
                  className="flex items-center justify-between p-4 rounded-2xl bg-white/50 border border-white/40 hover:bg-white/70 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-xl flex-shrink-0">
                      {exam.type === "behavioral" ? "🧠" : exam.type === "aptitude" ? "📐" : "📋"}
                    </div>
                    <div>
                      <p className="text-sm font-extrabold text-gray-800">{exam.title}</p>
                      <p className="text-xs text-gray-400">⏱ {exam.duration} min · ❓ {exam.totalQuestions} Qs</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge text={a.status} />
                    <Button size="sm" variant={a.status === "in_progress" ? "warning" : "primary"}
                      onClick={() => onNavigate("exams")}>
                      {a.status === "in_progress" ? "Resume" : "Start →"}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </GlassCard>

      {/* Recent results */}
      {myResults.length > 0 && (
        <GlassCard className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-extrabold text-gray-800">Recent Results</h3>
            <Button size="sm" variant="secondary" onClick={() => onNavigate("results")}>All Results →</Button>
          </div>
          <div className="space-y-3">
            {myResults.slice(0, 2).map(r => {
              const exam  = exams.find(e => e.id === r.examId);
              const color = r.percentage >= 70 ? "#10b981" : r.percentage >= 50 ? "#f59e0b" : "#ef4444";
              return (
                <div key={r.id} className="flex items-center gap-4 p-4 rounded-xl bg-white/40 border border-white/30">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-800 truncate">{exam?.title}</p>
                    <div className="mt-1.5">
                      <ProgressBar value={r.percentage} max={100} color={color} height={6} />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-2xl font-extrabold" style={{ color }}>{r.percentage}%</p>
                    <p className="text-xs text-gray-400">{r.totalScore}/{r.maxScore}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </GlassCard>
      )}
    </PageShell>
  );
};

export default CandidateDashboard;
